/*==============================================================================
██╗    ██╗ █████╗ ███╗   ██╗██████╗ ███████╗██████╗ ███████╗██████╗
██║    ██║██╔══██╗████╗  ██║██╔══██╗██╔════╝██╔══██╗██╔════╝██╔══██╗
██║ █╗ ██║███████║██╔██╗ ██║██║  ██║█████╗  ██████╔╝█████╗  ██████╔╝
██║███╗██║██╔══██║██║╚██╗██║██║  ██║██╔══╝  ██╔══██╗██╔══╝  ██╔══██╗
╚███╔███╔╝██║  ██║██║ ╚████║██████╔╝███████╗██║  ██║███████╗██║  ██║
 ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═══╝╚═════╝ ╚══════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝
==============================================================================*/
// import L from "leaflet";
import L from 'leaflet';
import { MaptilerLayer } from '@maptiler/leaflet-maptilersdk';
import { io } from 'socket.io-client';
import { JsonRpcProvider, parseEther } from 'ethers';
import { createPublicClient, createWalletClient, custom, http, parseGwei, } from 'viem';
import { wagmiContract } from './contractAbi';
import { sketchpad, forma } from 'viem/chains';
import { parseTransactionError } from './errorHandling';
const settings = {
    speedKmh: 5, // walking speed in km/h
    turnSpeed: 400, // time in ms to turn 90 degrees (only used with cone marker)
    markerZoomLimit: 22, // markers are shown up to and including this zoom level (zoom >= markerZoomLimit)
    recenterTimer: 20, // seconds to wait after user interaction before recentering the map
    doShowWalk: false,
    doShowOptions: false,
    debounceTime: 500, // ms for debouncing the get_markers_for_view calls
};
let CONTRACT_ADDRESS = '0x3237Ee723238443A180B84be4653236D07637066';
let API_URL = 'https://modularium-api.sketchpad-1.forma.art';
let MODULARIUM_URL = 'https://forma-devnet-modularspring-pr-95.onrender.com';
let EXPLORER_URL = 'https://explorer.sketchpad-1.forma.art';
let CHAIN = sketchpad;
let MINT_PRICE_ETH = '0.1';
if (process.env.NODE_ENV === 'production') {
    CONTRACT_ADDRESS = '0xffd96AD02fd872cB56713EA6978df66b436301Ca';
    API_URL = 'https://api.modularium.art';
    MODULARIUM_URL = 'https://modularium.art';
    EXPLORER_URL = 'https://explorer.forma.art';
    CHAIN = forma;
    MINT_PRICE_ETH = '1.0';
}
const ensProvider = new JsonRpcProvider('https://eth-mainnet.g.alchemy.com/v2/QxIgaAK_NyfD_kH4N4SWC');
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
let publicClient;
let walletClient;
// Socket.IO with CSRF token for origin verification
const csrfMeta = document.querySelector('meta[name="socket-csrf"]');
const socket = io({
    auth: {
        csrf: csrfMeta?.content || ''
    },
    transports: ['websocket', 'polling'],
});
// ==============================================================================
// Global variables
// ==============================================================================
let animationRunning = false;
const existingMarkerIds = new Set();
const existingMintedMarkerIds = new Set();
let animationRequestId = null;
let currentNode = null;
let dontUpdate = false;
let followResumeTimer = null; // Timer for resuming marker following after user pan/zoom en back to zoom 19
let followWanderer = true; // used by the automatic overview mode
let lastUpdatePosition = null;
// let prevNode: WandererNode | null = null;
let speedMps = 1.0; // meters per second (1.0 to prevent divide by zero)
let targetNode = null;
let wandererMarker = null; // the moving marker
let animatePath = []; // path between currentNode and targetNode
let animation_step_idx = 0; // index in the current animatePath
let wallet_connected = false;
let account = null; // address of the buyer
// Used to determine if the map movement is user-initiated or programmatic
// Sometimes we want to respond differently to these two types of movements
let isProgrammaticMove = true;
const expoMeta = document.querySelector('meta[name="expo-mode"]');
let expoMode = expoMeta?.content === "true" ? true : false;
const plotMeta = document.querySelector('meta[name="plot-mode"]');
const plotMode = plotMeta?.content === "true" ? true : false;
const blockchainDisabledMeta = document.querySelector('meta[name="blockchain-disabled"]');
const blockchainEnabled = blockchainDisabledMeta?.content !== "true";
const wandererLocationMeta = document.querySelector('meta[name="wanderer-location"]');
const wandererLocation = wandererLocationMeta?.content || "paris";
let thinkingTimer = null;
// ==============================================================================
// Admin debug panel (only shown when logged in / plotMode)
// ==============================================================================
let debugPanel = null;
function headingToCompass(heading) {
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return dirs[Math.round(heading / 45) % 8];
}
let debugStatsPanel = null;
let debugOverlayVisible = false;
function updateDebugPanel(debug) {
    if (!debugStatsPanel)
        return;
    const heading = debug.heading ?? debug.targetHeading;
    debugStatsPanel.innerHTML =
        `<div>path:    ${debug.pathHeading}° ${headingToCompass(debug.pathHeading)}</div>` +
            `<div>target:  ${debug.targetHeading}° ${headingToCompass(debug.targetHeading)}</div>` +
            `<div>heading: ${heading}° ${headingToCompass(heading)}</div>` +
            `<div>options: ${debug.candidates?.length ?? 0}</div>` +
            `<div>turn in: ${debug.stepsUntilTurn} steps</div>`;
}
function setDebugOverlayVisible(visible) {
    debugOverlayVisible = visible;
    if (visible) {
        debugOverlay.addTo(map);
        currentStepLayer.addTo(map);
    }
    else {
        map.removeLayer(debugOverlay);
        map.removeLayer(currentStepLayer);
    }
}
if (plotMode) {
    debugPanel = document.createElement('div');
    debugPanel.id = 'debugPanel';
    debugPanel.style.cssText = [
        'position:fixed',
        'bottom:24px',
        'right:12px',
        'background:rgba(0,0,0,0.6)',
        'color:#fff',
        'font:12px/1.6 monospace',
        'padding:6px 10px',
        'border-radius:4px',
        'z-index:9999',
    ].join(';');
    debugStatsPanel = document.createElement('div');
    debugStatsPanel.style.cssText = 'pointer-events:none';
    debugPanel.appendChild(debugStatsPanel);
    const toggleRow = document.createElement('label');
    toggleRow.style.cssText = 'display:block;margin-top:4px;cursor:pointer;pointer-events:auto;user-select:none';
    const toggleCheckbox = document.createElement('input');
    toggleCheckbox.type = 'checkbox';
    toggleCheckbox.checked = debugOverlayVisible;
    toggleCheckbox.style.cssText = 'margin-right:4px;vertical-align:middle';
    toggleCheckbox.addEventListener('change', () => setDebugOverlayVisible(toggleCheckbox.checked));
    toggleRow.appendChild(toggleCheckbox);
    toggleRow.appendChild(document.createTextNode('show debug paths'));
    debugPanel.appendChild(toggleRow);
    document.body.appendChild(debugPanel);
}
// ==============================================================================
// Map setup
// ==============================================================================
const key = 'G8Dj6RNXwSO9qObknfrH';
const styleUrl = 'https://api.maptiler.com/maps/0199ce3c-ce7b-7106-a454-44307ef76178/style.json';
const map = L.map('map').setView([48.866149, 2.313391], settings.markerZoomLimit);
const mtLayer = new MaptilerLayer({
    apiKey: key,
    style: styleUrl
}).addTo(map);
const nodeMarkersLayer = L.layerGroup(); // regular markers, only visible when zoomed in enough and doShowWalk is true
const mintedMarkersLayer = L.layerGroup().addTo(map); // always visible
const set_thinking_timer = () => {
    if (thinkingTimer) {
        clearTimeout(thinkingTimer);
    }
    thinkingTimer = setTimeout(() => {
        showNotification('thinking...', { loader: true, thinking: true });
    }, 2000);
};
const clear_thinkingTimer = () => {
    if (thinkingTimer) {
        clearTimeout(thinkingTimer);
    }
    document.querySelectorAll('.notification.thinking').forEach((el) => el.remove());
};
const get_bounding_box = (pad = true) => {
    if (map) {
        return pad ? map.getBounds().pad(0.25) : map.getBounds();
    }
    return null;
};
// Leading + trailing: fires immediately on the first call of a burst (so an isolated
// interaction feels instant), then suppresses further calls until things quiet down,
// then fires once more with the latest args if more calls came in during the cooldown.
function debounce(func, wait) {
    let timeout = null;
    let pendingArgs = null;
    return function (...args) {
        if (timeout === null) {
            func.apply(this, args);
        }
        else {
            pendingArgs = args;
        }
        if (timeout !== null) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            timeout = null;
            if (pendingArgs) {
                const a = pendingArgs;
                pendingArgs = null;
                func.apply(this, a);
            }
        }, wait);
    };
}
// Requests and responses can arrive out of order (response latency varies a lot with
// bbox size), so each request carries an increasing sequence number that the server
// echoes back. The markers_data handler discards any response that isn't the latest
// one sent, so a slow stale response can never overwrite a fresher one.
let markersRequestSeq = 0;
let latestMarkersRequestSeq = 0;
const get_markers_for_view = debounce((includeAnimationState) => {
    // console.log("get markers for view")
    if (dontUpdate)
        return;
    if (!socket || !map)
        return;
    // console.log('Requesting markers');
    latestMarkersRequestSeq = ++markersRequestSeq;
    socket.emit('get_markers', {
        isFollowing: followWanderer,
        showWalk: settings.doShowWalk,
        zoomLevel: map.getZoom(),
        boundingBox: get_bounding_box()?.toBBoxString(),
        includeAnimationState,
        requestSeq: latestMarkersRequestSeq,
    });
}, settings.debounceTime);
// =============================================================================
// Settings for paths and markers
// =============================================================================
// Regular junctions (blue circles)
const junctionMarkerSettings = {
    color: '#4386f2',
    fillColor: 'white',
    fillOpacity: 1,
    keyboard: false,
    pane: 'junctionPane',
    radius: 5,
    weight: 4,
};
// Minted junctions
const mintedJunctionMarkerSettings = {
    keyboard: false,
    pane: 'mintPane',
};
// Route lines
const routePolylineSettings = {
    interactive: false,
    pane: 'routesPane',
    color: '#4386f2',
    weight: 4,
    smoothFactor: 0.01, // done by server
};
// Color of animated path (for debugging)
// const animationPolylineSettings = {
//     ...routePolylineSettings,
//     weight: 8,
//     color: 'red',
// }
// Color of last segment towards wanderer (for debugging)
// const livePolylineSettings = {
//     ...routePolylineSettings,
//     weight: 12,
//     color: 'green',
// }
let linesGroup = L.layerGroup(); // The route as received from the server, based on the current view
let routePolyline = L.polyline([], routePolylineSettings);
linesGroup.addLayer(routePolyline);
// The route drawn by wanderer while watching; based on animationStates
// let animationPolyline = L.polyline([], settings.debug ? animationPolylineSettings : routePolylineSettings);
let animationPolyline = L.polyline([], routePolylineSettings);
linesGroup.addLayer(animationPolyline);
// The last part of the current animation segment, drawn to the wanderer marker
// let livePolyline = L.polyline([], settings.debug ? livePolylineSettings : routePolylineSettings);
let livePolyline = L.polyline([], routePolylineSettings);
linesGroup.addLayer(livePolyline);
const panes = [
    ['routesPane', 410], // below default markerPane (600)
    ['junctionPane', 520],
    ['movingPane', 620],
    ['mintPane', 680], // still below popupPane (700)
];
panes.forEach(([name, z]) => {
    const p = map.createPane(String(name));
    p.style.zIndex = String(z);
});
// Extend Leaflet Marker to add rotation functionality
class RotatableLeafletMarker extends L.Marker {
    _icon;
    constructor(latLon, settings) {
        super(latLon, settings);
        this.options.rotation = settings.rotation || 0;
    }
    _setPos(pos) {
        //@ts-ignore
        L.Marker.prototype._setPos.call(this, pos); // Ensure calling the original method
        this._setRotation(this.options.rotation);
    }
    _setRotation(rotation) {
        if (typeof rotation === 'number' && this._icon) { // Check if _icon is available
            this._icon.style[L.DomUtil.TRANSFORM + 'Origin'] = this.options.rotationOrigin || 'center';
            const transform = this._icon.style[L.DomUtil.TRANSFORM] + ` rotate(${rotation}deg)`;
            this._icon.style[L.DomUtil.TRANSFORM] = transform;
        }
    }
}
;
class JunctionMintedMarker extends L.Marker {
    constructor(latLon, settings) {
        super(latLon, settings); // Pass arguments to the parent class
    }
    _initIcon() {
        //@ts-ignore
        L.Marker.prototype._initIcon.call(this);
        //@ts-ignore
        this._icon.style.filter = 'hue-rotate(145deg)'; // red
    }
}
class JunctionCircleMarker extends L.CircleMarker {
    constructor(latLon, settings) {
        super(latLon, settings); // Pass arguments to the parent class
    }
}
const markerIcon = L.icon({
    iconUrl: '/static/images/pov_cone.png',
    iconSize: [125, 125],
    iconAnchor: [62, 62],
    className: 'marker-icon'
});
const WandererMarkerSettings = {
    icon: markerIcon,
    keyboard: false,
    pane: 'markerPane',
    rotation: 0,
    alt: '',
    interactive: false,
    bubblingMouseEvents: true,
};
const createWalkerMarker = () => {
    if (wandererMarker) {
        return;
    }
    wandererMarker = new RotatableLeafletMarker([currentNode.lat, currentNode.lon], WandererMarkerSettings).addTo(map);
    isProgrammaticMove = true;
    map.setView(wandererMarker.getLatLng(), settings.markerZoomLimit);
    // setView fires 'movestart' synchronously if it actually moves the map, which resets
    // isProgrammaticMove itself; but if the target equals the current view Leaflet no-ops
    // and never fires it, leaving the flag stuck true. Clear it here so a later real user
    // gesture (e.g. scroll-zoom) can never be misread as this programmatic move.
    isProgrammaticMove = false;
    lastUpdatePosition = wandererMarker.getLatLng();
};
const flytoButton = document.getElementById('flytoButton');
const toggleWalkButton = document.getElementById('toggleWalkButton');
const preload_image = (img_url) => {
    let img = new Image();
    img.src = img_url;
};
preload_image('/static/images/map-toggle-button-on.png');
preload_image('/static/images/map-toggle-button-off.png');
preload_image('/static/images/flyto.png');
const toggleDoShowWalk = (newValue = null) => {
    // Toggles options.doShowWalk between true and false or forces to newValue
    settings.doShowWalk = newValue !== null ? newValue : !settings.doShowWalk;
    if (toggleWalkButton) {
        toggleWalkButton.style.backgroundImage = `url("/static/images/map-toggle-button-${settings.doShowWalk ? 'on' : 'off'}.png")`;
    }
    if (settings.doShowWalk) {
        // request the markers and route
        // the response will handle adding them to the map
        if (map.getZoom() >= settings.markerZoomLimit && !map.hasLayer(nodeMarkersLayer)) {
            map.addLayer(nodeMarkersLayer);
        }
        if (!map.hasLayer(linesGroup)) {
            map.addLayer(linesGroup);
        }
        get_markers_for_view(false);
    }
    else {
        // Remove the markers and line groups from the map
        if (map.hasLayer(nodeMarkersLayer)) {
            map.removeLayer(nodeMarkersLayer);
        }
        if (map.hasLayer(linesGroup)) {
            map.removeLayer(linesGroup);
        }
    }
};
// Popups can be reopened for the same node/address repeatedly (e.g. re-clicking a
// marker); cache successful lookups so we don't re-fetch or re-run the retry loop
// each time. Failed lookups (data not indexed yet) are intentionally NOT cached,
// so a later popup open can retry and pick up newly-available data.
const imageCache = new Map();
const ownerCache = new Map();
const addressFormatCache = new Map();
async function fetchImage(url) {
    if (imageCache.has(url))
        return imageCache.get(url) ?? null;
    // console.log('fetchImage url:', url);
    try {
        let data = null;
        for (let attempt = 0; attempt < 20; attempt++) {
            const response = await fetch(url);
            data = await response.json();
            // console.log(data)
            if (data?.tokenMetadata?.image) {
                break;
            }
            await new Promise(resolve => setTimeout(resolve, 400));
        }
        // console.log('Fetched image:', data);
        const image = data?.tokenMetadata?.image || null;
        if (image)
            imageCache.set(url, image);
        return image;
    }
    catch (error) {
        console.error('Error fetching image:', error);
    }
}
async function fetchOwner(url) {
    if (ownerCache.has(url))
        return ownerCache.get(url) ?? null;
    try {
        let data = null;
        for (let attempt = 0; attempt < 20; attempt++) {
            const response = await fetch(url);
            data = await response.json();
            if (data?.[0]?.owner) {
                break;
            }
            await new Promise(resolve => setTimeout(resolve, 400));
        }
        // console.log('Fetched owner:', data);
        const owner = data?.[0]?.owner || null;
        if (owner)
            ownerCache.set(url, owner);
        return owner;
    }
    catch (error) {
        console.error('Error fetching owner:', error);
    }
}
const mintFeedbackSequence = (node_id) => {
    if (wallet_connected !== true) {
        console.error("wallet not connected, can't mint");
        showNotification('Wallet not connected, please connect your wallet first', {
            error: true
        });
        return;
    }
    if (socket) {
        socket.emit('get_minting_payload', { node_id: node_id, to_address: account });
    }
    else {
        console.error("no socket, can't mint");
        showNotification('Server error. Please reload the page and try again', {
            error: true
        });
    }
};
const resolveAddressToENS = async (address) => {
    // Returns the ENS name for the given address, or null if not found
    try {
        const ensName = await ensProvider.lookupAddress(address);
        return ensName ? ensName : null;
    }
    catch (error) {
        // console.log('Error resolving address:', error);
        return null;
    }
};
const format_address = async (address) => {
    // Swaps the address for the ENS name if available, otherwise shortens the address
    if (!address)
        return '';
    const cached = addressFormatCache.get(address);
    if (cached !== undefined)
        return cached;
    const ensName = await resolveAddressToENS(address);
    const formatted = ensName ? ensName : `0x...${address.slice(-4)}`;
    addressFormatCache.set(address, formatted);
    return formatted;
};
const add_node_marker = (node_markers, minted = false, open = false) => {
    // console.log('@add_node_marker: ', node_markers, minted, open)
    // Don't add regular markers if we're zoomed out too far
    if (!minted && map.getZoom() < settings.markerZoomLimit)
        return;
    // Make sure we have a list of markers
    if (!Array.isArray(node_markers)) {
        node_markers = [node_markers];
    }
    // Compute the padded view bounds once for the whole batch, instead of per marker
    const bounds = get_bounding_box(true);
    // Iterate over the markers list
    for (let i = 0; i < node_markers.length; i++) {
        const nm = node_markers[i];
        // Ignore markers outside the current view plus margin
        if (!bounds?.contains([nm.lat, nm.lon]))
            continue;
        // If we already have this marker, skip it
        if (!minted && existingMarkerIds.has(nm.node_id))
            continue;
        if (minted && existingMintedMarkerIds.has(nm.node_id))
            continue;
        let newMarker = null;
        if (!nm.minted) { // Unminted markers are simple circles
            newMarker = new JunctionCircleMarker([nm.lat, nm.lon], junctionMarkerSettings);
            if (blockchainEnabled)
                newMarker.bindPopup(() => {
                    if (plotMode) {
                        navigator.clipboard.writeText(nm.timestamp?.toString() || '');
                        showNotification(`${nm.timestamp?.toString() || ''} copied to clipboard`);
                    }
                    // console.log('node_id: ', nm.node_id)
                    const content = document.createElement('div');
                    content.className = 'mint-popup-content';
                    let mintButton = document.createElement('button');
                    mintButton.classList.add(`mintButton`);
                    mintButton.classList.add(`mint-button-${nm.node_id}`);
                    mintButton.textContent = 'mint junction';
                    mintButton.onclick = () => {
                        followWanderer = false;
                        if (followResumeTimer) {
                            clearTimeout(followResumeTimer);
                        }
                        mintButton.innerHTML = 'waiting to mint...<span class="spinner" aria-hidden="true"></span>';
                        mintButton.disabled = true;
                        const mintButtonErrorEl = content.querySelector('.mint-button-error');
                        if (mintButtonErrorEl) {
                            mintButtonErrorEl.textContent = '';
                            mintButtonErrorEl.style.display = 'none';
                        }
                        setTimeout(() => {
                            mintFeedbackSequence(nm.node_id);
                        }, 500);
                    };
                    mintButton.disabled = !wallet_connected;
                    content.appendChild(mintButton);
                    if (wallet_connected) {
                        let mintButtonErrorEl = document.createElement('p');
                        mintButtonErrorEl.className = 'mint-button-error';
                        content.appendChild(mintButtonErrorEl);
                    }
                    else {
                        let connectMsg = document.createElement('p');
                        connectMsg.className = 'connect-first-msg';
                        connectMsg.innerHTML = 'connect your wallet<br/>to mint this junction';
                        content.appendChild(connectMsg);
                    }
                    return content;
                }, {
                    minWidth: 150,
                    autoClose: false,
                    closeButton: false,
                });
            newMarker.addTo(nodeMarkersLayer);
            newMarker.options.node_id = nm.node_id;
            existingMarkerIds.add(nm.node_id);
        }
        else { // Minted markers
            newMarker = new JunctionMintedMarker([nm.lat, nm.lon], mintedJunctionMarkerSettings)
                .bindPopup(() => {
                if (plotMode) {
                    navigator.clipboard.writeText(nm.timestamp?.toString() || '');
                    showNotification(`${nm.timestamp?.toString() || ''} copied to clipboard`);
                }
                const content = document.createElement('div');
                const image = document.createElement('img');
                image.className = 'minted-junction-image';
                image.classList.add('hidden');
                image.style.width = '200px';
                image.style.height = '200px';
                image.style.border = '1px solid gainsboro';
                image.onload = () => {
                    image.classList.remove('hidden');
                    const spinner = content.querySelector('.popup-spinner');
                    if (spinner) {
                        spinner.remove();
                    }
                };
                // Link the image to the Modularium page
                const imageLink = document.createElement('a');
                imageLink.target = '_blank';
                imageLink.href = `${MODULARIUM_URL}/collection/wanderer/${nm.node_id}`;
                imageLink.rel = 'noopener noreferrer';
                imageLink.appendChild(image);
                const spinner = document.createElement('div');
                spinner.className = 'popup-spinner';
                spinner.innerHTML = '<span class="spinner" aria-hidden="true"></span>';
                content.appendChild(spinner);
                const linkEl = document.createElement('a');
                linkEl.target = '_blank';
                linkEl.innerHTML = `<span class="spinner" aria-hidden="true"></span>`;
                content.appendChild(imageLink);
                content.appendChild(document.createElement('br'));
                content.appendChild(linkEl);
                if (nm.owner) {
                    format_address(nm.owner).then((name) => {
                        linkEl.textContent = `Owned by ${name}`;
                        linkEl.href = `${MODULARIUM_URL}/user/${nm.owner}`;
                        linkEl.rel = 'noopener noreferrer';
                    });
                }
                else {
                    const ownerUrl = `${API_URL}/collection/${CONTRACT_ADDRESS}/${nm.node_id}/holders`;
                    fetchOwner(ownerUrl).then((ownerAddress) => {
                        if (ownerAddress) {
                            format_address(ownerAddress).then((name) => {
                                linkEl.textContent = `Owned by ${name}`;
                                linkEl.href = `${MODULARIUM_URL}/user/${ownerAddress}`;
                            });
                        }
                        else {
                            linkEl.textContent = `Owned by unknown`;
                            linkEl.removeAttribute('href');
                        }
                    });
                }
                const imageUrl = `${API_URL}/collection/${CONTRACT_ADDRESS}/${nm.node_id}`;
                fetchImage(imageUrl).then((imgSrc) => {
                    if (imgSrc)
                        image.src = imgSrc;
                });
                return content;
            }, {
                minWidth: 150,
                autoClose: false,
                closeButton: false,
            });
            newMarker.options.node_id = nm.node_id;
            newMarker.addTo(mintedMarkersLayer);
            existingMintedMarkerIds.add(nm.node_id);
            if (open) {
                isProgrammaticMove = true;
                map.setView([nm.lat, nm.lon], settings.markerZoomLimit, { animate: true, duration: 1.0 });
                isProgrammaticMove = false; // see comment in createWalkerMarker
                newMarker.openPopup();
            }
        }
    }
};
// Remove markers outside the current view, also remove their IDs from existingMarkerIds
const remove_out_of_bounds_markers = () => {
    // Compute the padded view bounds once for both passes, instead of per layer
    const bounds = get_bounding_box(true);
    // Remove regular junction markers
    nodeMarkersLayer.eachLayer((layer) => {
        const latLng = layer.getLatLng();
        if (!bounds?.contains(latLng)) {
            if (layer.options.node_id) {
                existingMarkerIds.delete(layer.options.node_id);
            }
            nodeMarkersLayer.removeLayer(layer);
        }
    });
    // Remove minted markers
    mintedMarkersLayer.eachLayer((layer) => {
        if (layer instanceof JunctionMintedMarker || layer instanceof JunctionCircleMarker) {
            if (!bounds?.contains(layer.getLatLng())) {
                if (layer.options.node_id) {
                    existingMintedMarkerIds.delete(layer.options.node_id);
                }
                mintedMarkersLayer.removeLayer(layer);
            }
        }
    });
};
const animateMarker = (timeIntoSegmentOffset = 0) => {
    // Runs once for each segment in the animatePath
    if (animation_step_idx >= animatePath.length - 1) {
        // Reached end of animated path: animation finished
        set_thinking_timer();
        animationRunning = false;
        animationRequestId = null;
        currentNode = targetNode;
        add_node_marker(currentNode);
        // Move lines from our animation into the route
        if (settings.doShowWalk && !animationPolyline.isEmpty()) {
            const currentLines = routePolyline.getLatLngs();
            if (animatePath.length > 0) {
                //@ts-ignore
                currentLines.push(animatePath);
            }
            routePolyline.setLatLngs(currentLines);
            // Clear the animation line and single line
            animationPolyline.setLatLngs([]);
            livePolyline.setLatLngs([]);
        }
        // Check if we've moved at least half a screen away from the last update position
        // before updating the visible markers
        if (wandererMarker && lastUpdatePosition) {
            const screenBounds = map.getBounds();
            const screenCenter = screenBounds.getCenter();
            const screenDistance = map.distance(screenBounds.getNorthWest(), screenCenter) / 2;
            const distance = map.distance(wandererMarker.getLatLng(), lastUpdatePosition);
            if (distance >= screenDistance) {
                lastUpdatePosition = wandererMarker.getLatLng();
                get_markers_for_view(false);
            }
        }
        return;
    }
    let from = animatePath[animation_step_idx];
    let to = animatePath[animation_step_idx + 1];
    let segmentDistance = map.distance(from, to);
    let duration = segmentDistance / speedMps * 1000; // ms
    let startTime = null; // passed when starting in the middle of a segment
    animationPolyline.addLatLng([from[0], from[1]]);
    let fromAngle = 0;
    let toAngle = 0;
    fromAngle = wandererMarker.options.rotation;
    const dx = to[1] - from[1];
    const dy = to[0] - from[0];
    toAngle = Math.atan2(dx, dy) * 180 / Math.PI;
    function step(ts) {
        // Runs on each frame of the animation for this segment
        if (!startTime)
            startTime = ts - timeIntoSegmentOffset;
        let elapsed = ts - startTime;
        let t = Math.min(elapsed / duration, 1);
        let lat = from[0] + (to[0] - from[0]) * t;
        let lon = from[1] + (to[1] - from[1]) * t;
        let tAngle = null;
        tAngle = Math.min(elapsed / settings.turnSpeed, 1);
        // Calculate shortest turn direction
        let delta = ((toAngle - fromAngle + 540) % 360) - 180;
        const newAngle = fromAngle + delta * tAngle;
        // Store the new angle; setLatLng will apply the actual rotation
        wandererMarker.options.rotation = newAngle;
        wandererMarker.setLatLng([lat, lon]);
        // Draw a single line from the last animation point to the wanderer
        livePolyline.setLatLngs([from, [lat, lon]]);
        if (followWanderer && wandererMarker) {
            isProgrammaticMove = true;
            map.panTo(wandererMarker.getLatLng(), { animate: false });
            isProgrammaticMove = false; // see comment in createWalkerMarker
        }
        if (t < 1) { // not at the end of this segment yet
            animationRequestId = requestAnimationFrame(step);
        }
        else { // segment is done, go to the next segment
            animation_step_idx++;
            animateMarker();
        }
    }
    animationRequestId = requestAnimationFrame(step);
};
const setAnimationState = (data, calculateOffset) => {
    if (animationRequestId) { // cancel the current animation request
        cancelAnimationFrame(animationRequestId);
    }
    clear_thinkingTimer();
    animationRunning = true;
    currentNode = data.from;
    targetNode = data.to;
    animatePath = targetNode.path.map((pt) => [pt.lat, pt.lon]);
    animation_step_idx = 0;
    // make sure there is wandererMarker
    if (!wandererMarker) {
        return;
    }
    if (!calculateOffset) {
        // just start at the beginning of the path
        animateMarker();
        return;
    }
    // Calculate where we are along the path based on time
    // how long has walker been walking this path?
    const timeSinceStart = Date.now() / 1000 - data.startTime;
    // how far (meters) should the walker have gone in that time?
    const distanceByTime = timeSinceStart * speedMps;
    let distanceChecked = 0.0;
    let segmentStartTime = 0.0;
    let timeIntoSegmentOffset = 0.0;
    // Iterate over the segments to find where we are
    for (let i = 0; i < animatePath.length - 1; i++) {
        const from = animatePath[i];
        const to = animatePath[i + 1];
        const segmentDistance = map.distance(from, to);
        if (distanceByTime < distanceChecked + segmentDistance) {
            animation_step_idx = i; // We're in segment i
            const distanceIntoSegment = distanceByTime - distanceChecked;
            timeIntoSegmentOffset = distanceIntoSegment / speedMps * 1000;
            // We need to offset our animation by <timeIntoSegmentOffset> ms for
            // the animation to match the actual position
            break;
        }
        // Update these after every check to prevent overshooting the target position
        distanceChecked += segmentDistance;
        segmentStartTime += segmentDistance / speedMps * 1000;
    }
    // We now know which segment we're in and how far into it we are
    // If needed, draw the previous segments into the animationPolyline
    // console.log("total path: ", animatePath.length, animatePath);
    // TODO: check if this works for all cases
    // const currentPath =
    //     animatePath.filter((_, idx) => idx < animation_step_idx).map((coord) => [coord[0], coord[1]]);
    const currentPath = animatePath.filter((_, idx) => idx < animation_step_idx);
    if (currentPath.length > 0)
        animationPolyline.setLatLngs(currentPath);
    // Use requestAnimationFrame to get the current animation timestamp
    animateMarker(timeIntoSegmentOffset);
};
const toggleExpoMode = (forceMode = null) => {
    if (forceMode !== null) {
        expoMode = forceMode;
    }
    else {
        expoMode = !expoMode;
    }
    if (expoMode) {
        followWanderer = true;
        document.body.classList.add('expo-mode');
        document.documentElement.requestFullscreen().catch(err => {
            console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
        });
        map.removeControl(map.zoomControl);
        toggleDoShowWalk(true);
        if (map.hasLayer(nodeMarkersLayer)) {
            map.removeLayer(nodeMarkersLayer);
        }
        if (map.hasLayer(mintedMarkersLayer)) {
            map.removeLayer(mintedMarkersLayer);
        }
    }
    else {
        document.body.classList.remove('expo-mode');
        document.exitFullscreen();
        map.addControl(map.zoomControl);
        if (settings.doShowWalk) {
            get_markers_for_view(false);
        }
    }
};
document.addEventListener('keydown', (event) => {
    if (event.key === 'e') {
        toggleExpoMode();
    }
    else if (event.key === 'f') {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        }
        else {
            document.exitFullscreen().catch(err => {
                console.error(`Error attempting to exit full-screen mode: ${err.message} (${err.name})`);
            });
        }
    }
});
const processFeedbackView = document.getElementById('processFeedbackView');
const showNotification = (msg, options = {}) => {
    if (expoMode || dontUpdate) {
        // No notifications in expo mode or when updates are disabled
        return;
    }
    let duration = 10000; // default duration 10 seconds
    if (options.duration) {
        duration = options.duration;
    }
    if (options.persistent || options.clearPersistent) {
        const persistentNotifications = document.querySelectorAll('.notification.persistent');
        persistentNotifications.forEach((notification) => notification.remove());
    }
    if (msg !== '') {
        const notification = document.createElement('div');
        notification.className = 'notification';
        notification.innerHTML = msg + (options.loader ? ' <span class="spinner" aria-hidden="true"></span>' : '');
        if (options.error)
            notification.classList.add('error');
        if (options.thinking)
            notification.classList.add('thinking');
        if (processFeedbackView) {
            processFeedbackView.appendChild(notification);
        }
        if (options.persistent || options.thinking) {
            notification.classList.add('persistent');
            return;
        }
        setTimeout(function () {
            notification?.remove();
            if (processFeedbackView?.children.length === 0) {
                processFeedbackView.style.display = 'none';
            }
        }, duration);
    }
};
const remove_single_marker = (node_id) => {
    // console.log('remove single marker')
    // Remove a single marker by node_id from either layer
    // Remove regular junction marker
    let removed = false;
    nodeMarkersLayer.eachLayer((layer) => {
        // console.log('layer: ', layer)
        if (layer.options.node_id === node_id) {
            nodeMarkersLayer.removeLayer(layer);
            existingMarkerIds.delete(layer.options.node_id);
            removed = true;
            return;
        }
    });
    if (!removed) {
        // Remove minted marker
        mintedMarkersLayer.eachLayer((layer) => {
            // console.log("type is: ", typeof layer);
            if (layer.options.node_id === node_id) {
                mintedMarkersLayer.removeLayer(layer);
                existingMintedMarkerIds.delete(layer.options.node_id);
                return;
            }
        });
    }
};
const mintedCallback = async (data) => {
    // console.log('@mintedCallback', data);
    if (data.error) {
        console.error(data.error);
        showNotification(`Error during minting: ${data.error}`, {
            error: true
        });
        return;
    }
    const { clientMinted, node_id, lat, lon, owner, txHash, silent } = data;
    if (clientMinted) { // If this client minted, tell others about this mint
        socket.emit('minted_ack', data);
    }
    // Remove the old junction marker (if exists) and replace with the new minted marker
    remove_single_marker(node_id);
    if (!data.silent) { // don't show this message on discovering an already minted junction
        if (clientMinted) { // show we minted
            if (txHash !== '') {
                showNotification(`minting completed! - <a class="explorerLink" href="${EXPLORER_URL}/tx/${txHash}" target="_blank">transaction</a>`, { clearPersistent: true });
            }
            else {
                showNotification(`minting completed!`, { clearPersistent: true });
            }
        }
        else { // show someone else minted
            const ownerFormatted = await format_address(owner);
            if (txHash) {
                showNotification(`${ownerFormatted} just minted! - <a class="explorerLink" href="${EXPLORER_URL}/tx/${txHash}" target="_blank">transaction</a>`);
            }
            else {
                showNotification(`${ownerFormatted} just minted!`);
            }
        }
        ;
    }
    add_node_marker({
        node_id: node_id,
        lat,
        lon,
        minted: true,
        owner: owner,
        txHash: data.txHash
    }, true, (clientMinted && !data.silent));
};
const lines = {
    pathOptionPolylinesGroup: null,
    graphAreaLines: [], // line from current position to the center of the graph area
    graphAreaRects: [], // rectangle showing the graph area
};
const graphLines = L.layerGroup();
const nodePoints = L.layerGroup();
const pathPoints = L.layerGroup();
// ==============================================================================
// Walker debug overlay: heading arrow + weighted candidate options (plotMode only)
// ==============================================================================
const debugOverlay = L.layerGroup();
if (plotMode)
    debugOverlay.addTo(map);
// Project a point distM meters from (lat,lon) along a compass bearing (0=N, 90=E),
// matching the backend's calculate_angle convention.
function destinationPoint(lat, lon, bearingDeg, distM) {
    const R = 6371000;
    const br = bearingDeg * Math.PI / 180;
    const lat1 = lat * Math.PI / 180;
    const lon1 = lon * Math.PI / 180;
    const dr = distM / R;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(br));
    const lon2 = lon1 + Math.atan2(Math.sin(br) * Math.sin(dr) * Math.cos(lat1), Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2));
    return [lat2 * 180 / Math.PI, lon2 * 180 / Math.PI];
}
// dirWeight: 3 = forward (green), 2 = sideways (amber), 1 = backward (red)
function dirWeightColor(dirWeight) {
    return dirWeight >= 3 ? '#2ecc40' : dirWeight === 2 ? '#ffb700' : '#ff4136';
}
// Persistent, faded record of every past step (accumulates into a trace).
// The bright detail of the current step lives in `currentStepLayer`.
const currentStepLayer = L.layerGroup();
if (plotMode)
    currentStepLayer.addTo(map);
// Faded contribution of one step to the accumulated trace: intended heading,
// the options that were available, and the option that was actually chosen.
function addTraceStep(debug) {
    if (!debug.from)
        return;
    const from = [debug.from.lat, debug.from.lon];
    const heading = debug.heading ?? debug.targetHeading;
    // faded intended-heading arrow (~40m)
    L.polyline([from, destinationPoint(from[0], from[1], heading, 40)], {
        color: '#00bfff',
        weight: 2,
        opacity: 0.3,
    }).addTo(debugOverlay);
    for (const c of debug.candidates ?? []) {
        const color = dirWeightColor(c.dirWeight);
        L.circleMarker([c.lat, c.lon], {
            radius: c.selected ? 3 : 2,
            color: c.selected ? '#ffffff' : color,
            weight: 1,
            fillColor: color,
            fillOpacity: c.selected ? 0.6 : 0.25,
        }).addTo(debugOverlay);
    }
    // thin white line tracing the walked path (from -> chosen node)
    const sel = (debug.candidates ?? []).find((c) => c.selected);
    if (sel) {
        L.polyline([from, [sel.lat, sel.lon]], {
            color: '#77ff00',
            weight: 4,
            opacity: 0.4,
        }).addTo(debugOverlay);
    }
}
// Bright, fully-annotated detail for the current step only.
function drawCurrentStep(debug) {
    currentStepLayer.clearLayers();
    if (!debug.from || !debug.candidates)
        return;
    const from = [debug.from.lat, debug.from.lon];
    const heading = debug.heading ?? debug.targetHeading;
    // Intended heading arrow (~60m)
    L.polyline([from, destinationPoint(from[0], from[1], heading, 60)], {
        color: '#00bfff',
        weight: 4,
        opacity: 0.9,
    }).addTo(currentStepLayer);
    for (const c of debug.candidates) {
        const color = dirWeightColor(c.dirWeight);
        // thin connector from current node to each candidate
        L.polyline([from, [c.lat, c.lon]], {
            color,
            weight: 1,
            opacity: 0.4,
            dashArray: '3 4',
        }).addTo(currentStepLayer);
        L.circleMarker([c.lat, c.lon], {
            radius: 4 + Math.sqrt(c.combinedWeight), // scale by selection chance (%)
            color: c.selected ? '#ffffff' : color, // selected gets a white ring
            weight: c.selected ? 3 : 1,
            fillColor: color,
            fillOpacity: c.visited ? 0.25 : 0.85, // visited = faded
        })
            .bindTooltip(`node ${c.node_id}<br/>` +
            `bearing ${c.bearing}° ${headingToCompass(c.bearing)}<br/>` +
            `angleDiff ${c.angleDiff}°<br/>` +
            `dirWeight ${c.dirWeight}<br/>` +
            `visited ${c.visited}<br/>` +
            `chance ${c.combinedWeight}%` +
            (c.selected ? '<br/><b>SELECTED</b>' : ''), { direction: 'top' })
            .addTo(currentStepLayer);
    }
}
// Live update: append this step to the trace and refresh the current-step detail.
function drawDebugOverlay(debug) {
    addTraceStep(debug);
    drawCurrentStep(debug);
}
// The server can emit 'animation_state' far faster than the display can usefully
// show it (observed bursts of hundreds/sec, way above any real walking step rate).
// Coalesce to at most one draw per animation frame, always using the latest debug
// payload so nothing goes stale — just drops redundant intermediate redraws.
let pendingDebugDraw = null;
let debugDrawScheduled = false;
function scheduleDebugOverlayDraw(debug) {
    pendingDebugDraw = debug;
    if (debugDrawScheduled)
        return;
    debugDrawScheduled = true;
    requestAnimationFrame(() => {
        debugDrawScheduled = false;
        if (pendingDebugDraw) {
            drawDebugOverlay(pendingDebugDraw);
            pendingDebugDraw = null;
        }
    });
}
// Replay a persisted trace (e.g. on connect). Clears, then accumulates all
// past steps faded, with the latest step shown in full bright detail.
function replayTrace(history) {
    debugOverlay.clearLayers();
    currentStepLayer.clearLayers();
    history.forEach((rec, i) => {
        addTraceStep(rec);
        if (i === history.length - 1)
            drawCurrentStep(rec);
    });
}
// ==============================================================================
// Map events
// ==============================================================================
let resetCenterTimerOnMoveEnd = false;
const mapInfoContainer = document.getElementById('mapInfoContainer');
// const updateMapInfo = () => {
//     if (!mapInfoContainer || !wandererMarker) {
//         return;
//     }
//     // const center = map.getCenter();
//     const zoom = map.getZoom();
//     const wandererLatLng = wandererMarker.getLatLng();
//     mapInfoContainer.innerHTML =
//         `Wanderer: ${wandererLatLng.lat.toFixed(5)}, ${wandererLatLng.lng.toFixed(5)} | Zoom: ${zoom.toFixed(2)}`;
// }
const resetViewToWanderer = () => {
    if (flytoButton) {
        flytoButton.style.display = 'none';
    }
    followWanderer = true;
    isProgrammaticMove = true;
    if (!wandererMarker) {
        return;
    }
    map.closePopup().setView(wandererMarker.getLatLng(), settings.markerZoomLimit);
    setTimeout(() => {
        get_markers_for_view(false);
    }, 200.0);
};
// Use Leaflet map event listeners for user interaction
map.on('movestart', () => {
    // console.log('@handleMoveStart, isProgrammaticMove: ', isProgrammaticMove)
    // Runs when move starts (pan or zoom)
    if (!isProgrammaticMove) {
        // console.log('user move detected, stopping follow');
        if (expoMode) {
            return;
        }
        followWanderer = false;
        if (followResumeTimer) {
            clearTimeout(followResumeTimer);
        }
        resetCenterTimerOnMoveEnd = true;
        if (flytoButton) {
            flytoButton.style.display = 'block';
        }
    }
    isProgrammaticMove = false;
})
    .on('moveend', () => {
    // Runs when move ends (both pan and zoom)
    // updateMapInfo();
    // console.log('at move end')
    if (!resetCenterTimerOnMoveEnd) {
        // was a programmatic move
        return;
    }
    // If we ended up zoomed out too far, remove the markers layer
    if (map.getZoom() < settings.markerZoomLimit) {
        // console.log('too far, hide markers')
        if (map.hasLayer(nodeMarkersLayer)) {
            map.removeLayer(nodeMarkersLayer);
        }
    }
    else if (settings.doShowWalk) {
        // add the markers layer back if needed
        if (!map.hasLayer(nodeMarkersLayer)) {
            map.addLayer(nodeMarkersLayer);
        }
    }
    get_markers_for_view(false);
    resetCenterTimerOnMoveEnd = false;
    if (followResumeTimer) {
        clearTimeout(followResumeTimer);
    }
    followResumeTimer = setTimeout(() => {
        resetViewToWanderer();
    }, settings.recenterTimer * 1000);
})
    .on('mousedown', () => {
    isProgrammaticMove = false;
    if (followResumeTimer) {
        clearTimeout(followResumeTimer);
    }
    if (!expoMode) {
        followWanderer = false;
        if (flytoButton) {
            flytoButton.style.display = 'block';
        }
    }
});
// =============================================================================
// Forma contract interaction
// =============================================================================
// The Wanderer Contract
const messagesEl = document.getElementById('messages');
const formEl = document.getElementById('chat-form');
const inputEl = document.getElementById('chat-input');
function addMessage(text) {
    const p = document.createElement('p');
    p.textContent = text;
    messagesEl.appendChild(p);
}
const updateMintButton = (mintBtn, msg, subMsg, disabled, error) => {
    if (mintBtn) {
        mintBtn.innerHTML = msg;
        mintBtn.disabled = disabled;
        if (error) {
            const mintButtonError = mintBtn.parentElement?.querySelector(`.mint-button-error`);
            if (mintButtonError) {
                mintButtonError.style.display = 'block';
                mintButtonError.textContent = subMsg;
            }
        }
    }
};
const mint = async (data) => {
    // console.log('minting with data:', data);
    const node_id = data.node_id;
    const inputData = [
        data.recipient, // Ethereum address
        data.junction, // Junction ID
        data.sentences, // Array of sentences
        data.coords, // Coordinates as string
        data.signature // signature
    ];
    const mintBtn = document.querySelector(`.mintButton.mint-button-${node_id}`);
    mintBtn.innerHTML = 'minting...<span class="spinner" aria-hidden="true"></span>';
    const transactionParameters = {
        address: CONTRACT_ADDRESS,
        abi: wagmiContract.abi,
        functionName: 'mint',
        args: [...inputData],
        gas: BigInt(300000),
        value: parseEther(MINT_PRICE_ETH),
        gasPrice: parseGwei('19'),
        account: data.recipient,
        chain: CHAIN, // Add the chain property here
    };
    // console.log("chain: ", CHAIN)
    try {
        // console.log('transactionParameters: ', transactionParameters)
        if (!walletClient || !publicClient) {
            console.error('Wallet or public client is not available');
            return;
        }
        try {
            await publicClient.simulateContract(transactionParameters);
        }
        catch (simErr) {
            const parsedError = parseTransactionError(simErr);
            if (!parsedError.passToWallet) { // Ignore errors that should be handled by the wallet
                throw simErr;
            }
        }
        const txHash = await walletClient.writeContract(transactionParameters);
        // console.log('txHash: ', txHash);
        updateMintButton(mintBtn, 'waiting for confirmation...<span class="spinner" aria-hidden="true"></span>', '', true, false);
        let succeeded = false;
        const transaction = await publicClient.waitForTransactionReceipt({ hash: txHash });
        if (transaction?.status === 'success') {
            succeeded = true;
        }
        else {
            // updateMintButton(mintBtn, 'transaction failed', 'possibly someone else minted this junction first', true, false);
            showNotification('Transaction failed: possibly someone else minted this junction first', { error: true });
            return;
        }
        if (txHash && succeeded) {
            mintedCallback({
                clientMinted: succeeded,
                node_id: node_id,
                lat: data.lat,
                lon: data.lon,
                owner: data.recipient,
                txHash: txHash
            });
        }
    }
    catch (err) {
        const parsedError = parseTransactionError(err);
        if (parsedError.buttonText) {
            updateMintButton(mintBtn, parsedError.buttonText, parsedError.message, !parsedError.canRetry, true);
        }
    }
};
const connectWallet = async () => {
    // close any popups that are open as they need to be updated after connect/disconnect
    map.closePopup();
    const connectBtn = document.getElementById('connectBtn');
    if (wallet_connected === true) { // already connected, so disconnect
        wallet_connected = false;
        account = null;
        connectBtn.innerText = 'connect';
        return;
    }
    connectBtn.innerHTML = 'connecting...<span class="spinner" aria-hidden="true"></span>';
    publicClient = createPublicClient({
        chain: CHAIN,
        transport: http()
    });
    walletClient = createWalletClient({
        chain: CHAIN,
        transport: custom(window.ethereum),
    });
    let address = null;
    try {
        [address] = await walletClient.requestAddresses();
    }
    catch (error) {
        connectBtn.innerText = 'connect';
        showNotification('Error connecting wallet', { error: true });
        return;
    }
    const chainId = await walletClient.getChainId();
    try {
        if (chainId !== CHAIN.id) {
            try {
                await walletClient.switchChain({ id: CHAIN.id });
            }
            catch {
                await walletClient.addChain({ chain: CHAIN });
            }
        }
    }
    catch (error) {
        console.error('Error switching or adding chain: ', error);
        connectBtn.innerText = 'connect';
        showNotification('Error switching wallet chain', { error: true });
        return;
    }
    account = address;
    const shortName = await format_address(address);
    connectBtn.innerText = `disconnect: ${shortName}`;
    toggleDoShowWalk(true); // show walk when connecting
    wallet_connected = true;
};
const updateAnimationState = (newAnimationState) => {
    if (newAnimationState.endTime > Date.now() / 1000) {
        // Animation is still running, calculate where to jump in
        setAnimationState(newAnimationState, true);
    }
    else {
        // Animation has already finished, just set to the target node
        currentNode = newAnimationState.to;
        targetNode = null;
        animatePath = [];
        animation_step_idx = 0;
        animationRunning = false;
        if (wandererMarker && currentNode) {
            wandererMarker.setLatLng([currentNode.lat, currentNode.lon]);
        }
        set_thinking_timer();
    }
};
window.addEventListener('DOMContentLoaded', () => {
    const warnEl = document.getElementById('metamask-warning');
    const connectBtn = document.getElementById('connectBtn');
    const infoButton = document.getElementById('infoButton');
    const infoView = document.getElementById('infoView');
    const infoViewCloseButton = document.getElementById('infoViewCloseButton');
    const infoViewMainText = document.getElementById('infoViewMainText');
    import(/* webpackMode: "eager" */ `./infotext_${wandererLocation}`)
        .then((mod) => {
        if (infoViewMainText) {
            infoViewMainText.innerHTML = mod.default;
        }
    })
        .catch(() => console.error(`No infotext_${wandererLocation}.ts found`));
    // ====================================================d==========================
    // Events setup
    // ==============================================================================
    // Start and stop updates when tab becomes hidden or visible
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            dontUpdate = true;
            if (animationRequestId) {
                cancelAnimationFrame(animationRequestId);
            }
        }
        else {
            dontUpdate = false;
            get_markers_for_view(true);
        }
    });
    infoButton?.addEventListener('click', () => {
        if (infoView) {
            infoView.style.display = 'block';
        }
    });
    infoViewCloseButton?.addEventListener('click', () => {
        if (infoView) {
            infoView.style.display = 'none';
        }
    });
    toggleWalkButton?.addEventListener('click', function () {
        toggleDoShowWalk();
    });
    flytoButton?.addEventListener('click', function () {
        resetViewToWanderer();
    });
    if (blockchainEnabled && window.ethereum) {
        // check if Ethereum provider comes from MetaMask
        if (window.ethereum.isMetaMask) {
            // console.log('Connected to MetaMask Ethereum provider');
            if (connectBtn) {
                connectBtn.disabled = false;
                connectBtn?.addEventListener('click', connectWallet);
            }
        }
        else {
            // console.log('Connected to Non-MetaMask Ethereum provider');
            warnEl.innerHTML = "Please <a href='https://metamask.io/download/'>install MetaMask</a>.";
            connectBtn.disabled = true;
        }
    }
    else if (blockchainEnabled) { // no Ethereum provider - instruct user to install MetaMask
        // console.log('No Ethereum provider detected');
        if (isMobile) {
            connectBtn?.addEventListener('click', () => {
                window.location.href = 'https://metamask.app.link/dapp/wanderer.leegte.org';
            });
            connectBtn.innerText = 'Open MetaMask Mobile';
            connectBtn.disabled = false;
        }
        else {
            warnEl.innerHTML = "Please <a href='https://metamask.io/download/'>install MetaMask</a>.";
            connectBtn.disabled = true;
        }
    }
    // =============================================================================
    // Socket.IO events
    // =============================================================================
    let socketErrorCount = 0;
    let lastSocketErrorTime = 0;
    socket.on('connect_error', (error) => {
        const now = Date.now();
        if (now - lastSocketErrorTime < 60000) {
            lastSocketErrorTime = now;
            socketErrorCount++;
            if (socketErrorCount >= 5) {
                // console.warn('Too many errors, reloading the page.');
                // if (!window.location.hostname.includes('localhost')) {
                //     window.location.href = "https://wanderer.leegte.org";
                // }
            }
        }
        else {
            lastSocketErrorTime = now;
            socketErrorCount = 1;
        }
    });
    socket.on('minting_payload', (data) => {
        // console.log('@minting_payload', data);
        if (data.error) {
            console.error(data.error);
            showNotification(`Error preparing minting: ${data.error}`, {
                error: true
            });
            return;
        }
        mint(data);
    });
    socket.on('markers_data', (data) => {
        // console.log('Received markers data', data);
        // Discard stale responses: if a newer request has been sent since this one,
        // this response no longer reflects the current view and must not overwrite it.
        if (data.requestSeq !== undefined && data.requestSeq !== latestMarkersRequestSeq)
            return;
        // Remove any markers that are out of bounds
        remove_out_of_bounds_markers();
        // Check what markers we currently have that are not in the new set
        const newNodes = data.markers.filter((m) => !existingMarkerIds.has(m.node_id));
        add_node_marker(newNodes, false);
        const newMintedNodes = data.mintedMarkers.filter((m) => !existingMintedMarkerIds.has(m.node_id));
        // console.log('new Minted Nodes: ', newMintedNodes);
        add_node_marker(newMintedNodes, true);
        // console.log("got new route")
        // Update the route polyline (can also be empty)
        if (data.animationState)
            updateAnimationState(data.animationState);
        let route = data.route || [];
        if (!animationRunning && animatePath.length > 0)
            route.push(animatePath);
        if (route.length > 1)
            routePolyline.setLatLngs(route);
    });
    socket.on('start_data', (data) => {
        // Just connected or server restarted
        // Stuff for debugging the graph
        graphLines.clearLayers();
        nodePoints.clearLayers();
        pathPoints.clearLayers();
        if (wandererMarker)
            lastUpdatePosition = wandererMarker.getLatLng();
        speedMps = data.settings.speedKmh * 1000 / 3600;
        settings.markerZoomLimit = data.settings.markerZoomLimit;
        currentNode = data.current_node;
        if (currentNode) {
            if (data.current_node.rotation) {
                WandererMarkerSettings.rotation = data.current_node.rotation;
            }
            createWalkerMarker();
        }
        if (data.animationState) {
            updateAnimationState(data.animationState);
        }
        if (plotMode) {
            // Replay the full persisted trace so the whole history is visible on connect.
            if (data.traceHistory && data.traceHistory.length) {
                replayTrace(data.traceHistory);
                updateDebugPanel(data.traceHistory[data.traceHistory.length - 1]);
            }
            else if (data.debug) {
                updateDebugPanel(data.debug);
                drawDebugOverlay(data.debug);
            }
        }
        // Request the markers for the current view
        get_markers_for_view(false);
    });
    socket.on('animation_state', (data) => {
        if (dontUpdate)
            return;
        const whenStartNotification = document.querySelector('.when-start-notification');
        whenStartNotification?.remove();
        setAnimationState(data, false);
        if (plotMode && data.debug) {
            updateDebugPanel(data.debug);
            scheduleDebugOverlayDraw(data.debug);
        }
    });
    socket.on('node_minted', (data) => {
        // Other user minted a junction, or an already minted node was discovered
        // data.silent means this client is the one who tried to mint an already
        // minted junction. They already see an error in the mint popup, so delay
        // this new marker to prevent the popup from closing too quickly
        if (data.silent) {
            setTimeout(() => {
                mintedCallback(data);
            }, 3000);
            return;
        }
        // For other users, just update the marker immediately
        mintedCallback(data);
    });
    socket.on('staggered_reload', ({ startAt, windowMs, groups = 20 }) => {
        // Simple deterministic hash from socket.id
        const hash = [...(socket.id || '')].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0);
        const bucket = Math.abs(hash) % groups;
        const perBucket = Math.max(1, Math.floor(windowMs / groups));
        const target = startAt + bucket * perBucket;
        const delay = Math.max(0, target - Date.now());
        console.log(`Reload requested by server, waiting for ${delay / 1000} seconds`);
        setTimeout(() => window.location.reload(), delay);
    });
    // =============================================================================
    // Finally, enable expo mode if needed
    // =============================================================================
    if (expoMode) {
        toggleExpoMode(true);
    }
}); // DOMContentLoaded
